import React, { useState, useMemo } from 'react';
import { isNil } from 'lodash';
import classNames from 'classnames';
import { InvoiceBillAndShipTo, Refunds } from '@css-xp/graphtypes/src/generated-types-new';
import { NEW_DATE_FORMAT, NEW_DATE_TIME_FORMAT } from '@atlassian/cssxp-typescommon';
import { RowType } from '@atlaskit/dynamic-table/dist/types/types';
import {
  CustomizeView,
  DynamicResultTable,
  FormattedAddress,
  FormattedDate,
  HeadCell,
  RefundedLozenge,
} from '@css-xp/base';
import {
  SoldToAddressDetailsLink,
  emptyStateSearchTabs,
  getSelectedColumns,
  FormatCurrency,
  errorStateProp,
  NoData,
  PPC_TABS,
  TableHeading,
} from '@css-xp/ppc-common';
import { InvoiceRefundStatusLozenge } from '@css-xp/billing-v2';
import { RefundDetailsLink } from '@css-xp/ppc-common/src/refund-details-link';
import { emptyStatePropForWrongSearch } from '@css-xp/ppc-common/src/state-props';
import './refund-table.scss';

export interface RefundTableProps {
  refunds: Refunds[];
  transactionAccountId: string;
  isLoading: boolean;
  error?: any;
  shouldHideCustomiseView?: boolean;
  shouldHideInnerHeading?: boolean;
  shouldHideFilters?: boolean;
  isDesignIteration1Enabled?: boolean;
  columnsToShow?: string[];
}

enum TableColumns {
  CREDIT_NOTE_NUMBER = 'Credit note number',
  COMPANY = 'Company',
  SOLD_TO_ADDRESS = 'Sold-to address',
  STATUS = 'Status',
  ISSUED_ON = 'Issued on',
  REQUESTED_BY = 'Requested By',
  AMOUNT = 'Amount',
  ISSUED = 'Issued',
  CUSTOMER = 'Customer',
  PO_NUMBER = 'PO number',
}

type ColumnList = HeadCell;
type HeaderMapType = {
  [key: string]: {
    content: TableColumns | React.ReactNode;
    customClassName?: string;
    isMandatory?: boolean;
    isChecked?: boolean;
    className?: string;
  };
};

// default columns to show for design iteration 1
const defaultColumns = [
  TableColumns.CREDIT_NOTE_NUMBER,
  TableColumns.STATUS,
  TableColumns.AMOUNT,
  TableColumns.ISSUED,
  TableColumns.CUSTOMER,
  TableColumns.PO_NUMBER,
];

const wrapInCell = (node?: React.ReactElement | string | null) => {
  return <div className="refund-cell">{node}</div>;
};

const headersMap = (isDesignIteration1Enabled?: boolean): HeaderMapType => {
  return {
    [TableColumns.CREDIT_NOTE_NUMBER]: {
      content: TableColumns.CREDIT_NOTE_NUMBER,
      customClassName: 'refund-cell',
    },
    [TableColumns.COMPANY]: {
      content: TableColumns.COMPANY,
      customClassName: 'refund-cell',
    },
    [TableColumns.SOLD_TO_ADDRESS]: {
      content: TableColumns.SOLD_TO_ADDRESS,
      customClassName: 'refund-cell',
    },
    [TableColumns.STATUS]: {
      content: TableColumns.STATUS,
    },
    [TableColumns.ISSUED_ON]: {
      content: TableColumns.ISSUED_ON,
    },
    [TableColumns.REQUESTED_BY]: {
      content: TableColumns.REQUESTED_BY,
      isMandatory: false,
      isChecked: true,
    },
    [TableColumns.AMOUNT]: {
      content: TableColumns.AMOUNT,
      className: isDesignIteration1Enabled ? 'text-align-right col-10' : '',
    },
    [TableColumns.ISSUED]: {
      content: TableColumns.ISSUED,
    },
    [TableColumns.CUSTOMER]: {
      content: TableColumns.CUSTOMER,
    },
    [TableColumns.PO_NUMBER]: {
      content: TableColumns.PO_NUMBER,
    },
  };
};

const getSoldToAddress = (
  shipTo: InvoiceBillAndShipTo | undefined | null,
  transactionAccountId: string,
  isDesignIteration1Enabled?: boolean
) => {
  if (!shipTo?.address) {
    return <NoData />;
  } else {
    return (
      <SoldToAddressDetailsLink
        txaId={transactionAccountId}
        shipToId={shipTo.id}
        shouldDisableLink={isDesignIteration1Enabled}
      >
        <span className="line-clamp">
          <FormattedAddress
            addressLine1={shipTo.address?.address1}
            addressLine2={shipTo.address?.address2}
            city={shipTo.address?.city}
            country={shipTo.address?.country}
            postcode={shipTo.address?.postalCode}
            state={shipTo.address?.state}
          />
        </span>
      </SoldToAddressDetailsLink>
    );
  }
};

const getCompanyName = (shipTo: InvoiceBillAndShipTo | undefined | null, transactionAccountId: string) => {
  if (!shipTo?.fullName) {
    return <NoData />;
  } else {
    return (
      <SoldToAddressDetailsLink txaId={transactionAccountId} companyName={shipTo.fullName}>
        <span className="line-clamp">
          <FormattedAddress organizationName={shipTo.fullName} />
        </span>
      </SoldToAddressDetailsLink>
    );
  }
};

const makeColumns = (
  refunds: Refunds[],
  transactionAccountId: string,
  visibleColumns: ColumnList[],
  isDesignIteration1Enabled?: boolean
) => {
  return refunds?.map((refund: Refunds, index) => {
    const rowKey = `refund-row-${index}`;
    return {
      key: rowKey,
      cells: visibleColumns.map((column) => {
        switch (column.content) {
          case TableColumns.CREDIT_NOTE_NUMBER:
            return {
              content: wrapInCell(
                refund.creditNoteNumber ? (
                  <RefundDetailsLink refundNumber={refund.creditNoteNumber} txaId={transactionAccountId} />
                ) : (
                  <NoData />
                )
              ),
            };
          case TableColumns.CUSTOMER:
          case TableColumns.COMPANY:
            return {
              content: wrapInCell(getCompanyName(refund.shipTo, transactionAccountId)),
            };
          case TableColumns.SOLD_TO_ADDRESS:
            return {
              content: wrapInCell(getSoldToAddress(refund.shipTo, transactionAccountId, isDesignIteration1Enabled)),
            };
          case TableColumns.STATUS:
            return {
              content: wrapInCell(refund.arn ? <InvoiceRefundStatusLozenge status="PENDING" /> : <RefundedLozenge />),
            };
          case TableColumns.ISSUED_ON:
            return {
              content: wrapInCell(
                refund.createdDate && (
                  <FormattedDate date={refund.createdDate} showTime={true} format={NEW_DATE_TIME_FORMAT} />
                )
              ),
            };
          case TableColumns.REQUESTED_BY:
            return {
              content: wrapInCell(
                refund.requestedBy ? <span className="email">{refund.requestedBy}</span> : <NoData />
              ),
            };
          case TableColumns.AMOUNT:
            return {
              content: wrapInCell(
                refund.total ? (
                  <div className={classNames({ 'text-align-right': isDesignIteration1Enabled })}>
                    <FormatCurrency value={refund.total} currency={refund.currency} showCurrencyCode showCommas />
                  </div>
                ) : (
                  <NoData />
                )
              ),
            };
          case TableColumns.ISSUED:
            return {
              content: wrapInCell(
                refund.createdDate ? <FormattedDate date={refund.createdDate} format={NEW_DATE_FORMAT} /> : <NoData />
              ),
            };
          case TableColumns.PO_NUMBER:
            return {
              content: wrapInCell(!isNil(refund?.purchaseOrderNumber) ? refund.purchaseOrderNumber : <NoData />),
            };
        }
      }),
    };
  });
};

export const RefundTable = (refundTableProps: RefundTableProps) => {
  const {
    refunds,
    error,
    transactionAccountId,
    isLoading,
    shouldHideCustomiseView,
    shouldHideInnerHeading,
    shouldHideFilters,
    isDesignIteration1Enabled,
    columnsToShow = defaultColumns,
  } = refundTableProps;

  const getHead = useMemo(() => {
    const columnHeaders = headersMap(isDesignIteration1Enabled);

    if (isDesignIteration1Enabled) {
      const updatedColumns = columnsToShow.map((column) => columnHeaders[column]);
      return updatedColumns;
    }

    const allColumns = [
      columnHeaders[TableColumns.CREDIT_NOTE_NUMBER],
      columnHeaders[TableColumns.COMPANY],
      columnHeaders[TableColumns.SOLD_TO_ADDRESS],
      columnHeaders[TableColumns.STATUS],
      columnHeaders[TableColumns.ISSUED_ON],
      columnHeaders[TableColumns.REQUESTED_BY],
      columnHeaders[TableColumns.AMOUNT],
    ];
    return allColumns;
  }, [columnsToShow, isDesignIteration1Enabled]);

  const [columnsList, setColumnList] = useState<Array<ColumnList>>(getHead);
  return (
    <div className="refunds-container">
      <DynamicResultTable
        testId={'refund'}
        customiseColumnView={
          shouldHideCustomiseView ? undefined : (
            <CustomizeView
              columns={columnsList}
              columnCount={columnsList.length}
              updatedColumnView={(record: Array<HeadCell>) => setColumnList([...record] as Array<ColumnList>)}
            />
          )
        }
        beforeTable={shouldHideInnerHeading ? <></> : <TableHeading>{PPC_TABS.REFUNDS}</TableHeading>}
        head={{ cells: getSelectedColumns(columnsList) }}
        rows={
          makeColumns(
            refunds,
            transactionAccountId,
            getSelectedColumns(columnsList),
            isDesignIteration1Enabled
          ) as RowType[]
        }
        emptyStateProps={isDesignIteration1Enabled ? emptyStatePropForWrongSearch : emptyStateSearchTabs}
        error={error?.toString()}
        errorStateProps={errorStateProp}
        isLoading={isLoading}
        showFilters={!shouldHideFilters}
        hideHeadOnEmptyState={isDesignIteration1Enabled}
      />
    </div>
  );
};
